import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router'
import { flowProgress, useSessionStore, type FlowStep } from '@/features/application-session'
import { SidebarStepper, type SidebarStep } from '@/shared/components/SidebarStepper'
import { StepperSuppressionProvider } from '@/shared/components/stepperVisibility'

/**
 * The "Application Progress" shell around the journey. The URL decides which
 * step is current; the session store decides which are complete
 * (selectors/flowProgress.ts).
 */
function currentStepFromPath(pathname: string): FlowStep {
  if (pathname.endsWith('/id-number')) return 'idNumber'
  // The eligibility interstitial belongs to step 1: it narrates the §4.1 check
  // the ID-number screen just made, and that step completes the instant the
  // reference number exists — which is exactly when this screen is reached.
  if (pathname.endsWith('/eligibility')) return 'idNumber'
  if (pathname.endsWith('/verify') || pathname.endsWith('/unable-to-verify')) return 'face'
  if (pathname.endsWith('/details')) return 'details'
  if (pathname.endsWith('/contact')) return 'contact'
  if (pathname.endsWith('/address')) return 'address'
  // Ahead of the '/verify' test above only in intent, not in order:
  // '/physical-verification' does not end in '/verify', so the two cannot collide.
  if (pathname.endsWith('/officer')) return 'officer'
  if (pathname.endsWith('/physical-verification')) return 'physicalVerification'
  if (pathname.endsWith('/review')) return 'review'
  if (pathname.endsWith('/declaration')) return 'declaration'
  if (pathname.endsWith('/submitted')) return 'submitted'
  return 'idNumber'
}

/**
 * The ten rows the panel draws, each pointing at the FLOW_STEPS entry whose
 * completion it reports.
 *
 * One row per flow step, in the same order — the display list and FLOW_STEPS no
 * longer disagree about how long the journey is, so nothing here has to
 * reconcile them.
 *
 * `labelKey` is spelled out rather than derived from the id: the lookup below
 * casts its key, which defeats the typed-key check, so an id with no catalogue
 * entry would compile and then throw at render (src/i18n/index.ts).
 */
const DISPLAY_STEPS = [
  { id: 'idNumber', source: 'idNumber', labelKey: 'apply:steps.idNumber.label' },
  { id: 'face', source: 'face', labelKey: 'apply:steps.face.label' },
  { id: 'details', source: 'details', labelKey: 'apply:steps.details.label' },
  { id: 'contact', source: 'contact', labelKey: 'apply:steps.contact.label' },
  { id: 'address', source: 'address', labelKey: 'apply:steps.address.label' },
  { id: 'officer', source: 'officer', labelKey: 'apply:steps.officer.label' },
  {
    id: 'physicalVerification',
    source: 'physicalVerification',
    labelKey: 'apply:steps.physicalVerification.label',
  },
  { id: 'review', source: 'review', labelKey: 'apply:steps.review.label' },
  { id: 'declaration', source: 'declaration', labelKey: 'apply:steps.declaration.label' },
  { id: 'submitted', source: 'submitted', labelKey: 'apply:steps.submitted.label' },
] as const satisfies ReadonlyArray<{ id: string; source: FlowStep; labelKey: string }>

export function SidebarLayout() {
  const { t } = useTranslation(['apply'])
  const { pathname } = useLocation()

  const appRefNo = useSessionStore((s) => s.appRefNo)
  const faceVerified = useSessionStore((s) => s.faceVerified)
  const completedSteps = useSessionStore((s) => s.completedSteps)
  const submittedAt = useSessionStore((s) => s.submittedAt)

  // Application Submitted suppresses the panel from inside the page, because the
  // Submitting interstitial that precedes it on the same route keeps it. See
  // stepperVisibility.ts.
  const [suppressed, setSuppressed] = useState(false)

  const currentStep = currentStepFromPath(pathname)

  const progress = flowProgress(
    { appRefNo, faceVerified, completedSteps, submittedAt },
    currentStep
  )

  const steps: SidebarStep[] = DISPLAY_STEPS.map(({ id, source, labelKey }) => ({
    id,
    status: progress.find((step) => step.id === source)?.status ?? 'pending',
    label: t(labelKey as 'apply:steps.face.label'),
  }))

  const completedCount = steps.filter((step) => step.status === 'complete').length

  // Every route keeps the panel; only the receipt drops it, and it says so from
  // inside the page because the Submitting interstitial that precedes it on the
  // same route keeps the panel. See stepperVisibility.ts.
  const withStepper = !suppressed

  /**
   * "Reset Application" used to sit here, under the step's card. It has moved
   * into the header (PublicLayout), which is where the board puts an
   * application-level action and where it no longer competes with the step's own
   * primary CTA. The "not on the receipt" rule moved with it.
   */
  const body = (
    <div className="flex min-w-0 flex-col gap-3">
      <StepperSuppressionProvider value={setSuppressed}>
        <Outlet />
      </StepperSuppressionProvider>
    </div>
  )

  /**
   * ONE tree whose class list changes, never two trees returned from different
   * branches — and the stepper stays a conditional slot rather than disappearing
   * from the child list.
   *
   * Returning a separate centred element moved `body` from the second child to
   * the first, which React reconciles as a different node and remounts, wiping
   * whatever state the page below was holding. That is not theoretical: the
   * Submitting Application interstitial suppresses the panel at the moment it
   * hands over to the receipt, so the remount reset the very flag that had
   * suppressed it, the panel came back, and the screen looped forever. Any page
   * that both holds state and suppresses the panel would hit the same wall.
   *
   * Centred: MainPanel inside the page already caps its own width, so the class
   * only has to stop the column stretching to the full 1200px canvas.
   * With the panel: 246 + 24 + 930 = the 1200px content column of the frames.
   */
  return (
    <div
      className={
        withStepper
          ? 'flex flex-col gap-3 lg:grid lg:grid-cols-[var(--width-sidebar)_minmax(0,var(--container-main))] lg:items-start lg:justify-center lg:gap-3'
          : 'flex flex-col items-center'
      }
    >
      {withStepper && (
        <SidebarStepper
          steps={steps}
          completedCount={completedCount}
          className="lg:sticky lg:top-3"
        />
      )}
      {body}
    </div>
  )
}
